import React, { useMemo } from 'react';
import { formatDate as formatDateGlobal } from '../../../config/appConfig';
import {
  ReportField,
  ReportFlowPages,
  ReportFooter,
  ReportHeader,
  ReportMeta,
  ReportMetaRow,
  ReportNote,
  ReportSignLine,
  ReportSigns,
  ReportTitleRow,
} from '../../../components/ui/Report';
import {
  MONTH_BUCKETS,
  buildOnboardCountRows,
  type OnboardCountRow,
} from '../OnboardStatisticsPage';
import type { CrewStatsOnboardRow } from '../types';

function fmtDate(v?: string | null) {
  return v ? formatDateGlobal(v) : '—';
}

function num(n: number) {
  return n === 0 ? '—' : String(n);
}

function TableHead({ showUnknown }: { showUnknown: boolean }) {
  return (
    <table className="ob-count-print-table ob-count-print-table-head">
      <thead>
        <tr>
          <th className="ob-count-col-vessel">Vessel</th>
          <th className="ob-count-col-nat">Nationality</th>
          {MONTH_BUCKETS.map((b) => (
            <th key={b.key} className="ob-count-col-num">
              {b.label}
            </th>
          ))}
          {showUnknown ? <th className="ob-count-col-num">No sign-on</th> : null}
          <th className="ob-count-col-num">Total</th>
        </tr>
      </thead>
    </table>
  );
}

function LineRow({ row, showUnknown }: { row: OnboardCountRow; showUnknown: boolean }) {
  return (
    <table className="ob-count-print-table ob-count-print-row-table">
      <tbody>
        <tr>
          <td className="ob-count-col-vessel">{row.vessel}</td>
          <td className="ob-count-col-nat">{row.nationality}</td>
          {MONTH_BUCKETS.map((b) => (
            <td key={b.key} className="ob-count-col-num">
              {num(row[b.key])}
            </td>
          ))}
          {showUnknown ? <td className="ob-count-col-num">{num(row.unknown)}</td> : null}
          <td className="ob-count-col-num ob-count-col-total">{row.total}</td>
        </tr>
      </tbody>
    </table>
  );
}

function TotalsRow({ rows, showUnknown }: { rows: OnboardCountRow[]; showUnknown: boolean }) {
  const totals = rows.reduce(
    (acc, row) => {
      acc.m0_3 += row.m0_3;
      acc.m3_6 += row.m3_6;
      acc.m6_9 += row.m6_9;
      acc.m9_12 += row.m9_12;
      acc.m12 += row.m12;
      acc.unknown += row.unknown;
      acc.total += row.total;
      return acc;
    },
    { m0_3: 0, m3_6: 0, m6_9: 0, m9_12: 0, m12: 0, unknown: 0, total: 0 },
  );
  return (
    <table className="ob-count-print-table ob-count-print-totals-table">
      <tbody>
        <tr>
          <td className="ob-count-col-vessel" colSpan={2}>
            Total
          </td>
          {MONTH_BUCKETS.map((b) => (
            <td key={b.key} className="ob-count-col-num">
              {totals[b.key]}
            </td>
          ))}
          {showUnknown ? <td className="ob-count-col-num">{totals.unknown}</td> : null}
          <td className="ob-count-col-num ob-count-col-total">{totals.total}</td>
        </tr>
      </tbody>
    </table>
  );
}

export type OnboardCountReportProps = {
  onboard: CrewStatsOnboardRow[];
  vesselLabel: string;
};

export const OnboardCountReport: React.FC<OnboardCountReportProps> = ({ onboard, vesselLabel }) => {
  const rows = useMemo(() => buildOnboardCountRows(onboard), [onboard]);
  const showUnknown = rows.some((r) => r.unknown > 0);
  const vesselCount = new Set(rows.map((r) => r.vessel)).size;
  const nationalityCount = new Set(rows.map((r) => r.nationality)).size;

  const firstHeader = useMemo(
    () => (
      <div className="ob-count-print-flow-header">
        <ReportHeader>
          <ReportTitleRow title="Onboard — nationality and months" formId="Statistics" />
          <ReportMeta>
            <ReportMetaRow>
              <ReportField label="Vessel" value={vesselLabel} />
              <ReportField label="Crew" value={String(onboard.length)} />
              <ReportField label="Nationalities" value={String(nationalityCount)} />
            </ReportMetaRow>
            <ReportMetaRow>
              <ReportField label="Vessels" value={String(vesselCount)} />
              <ReportField label="Printed" value={fmtDate(new Date().toISOString().slice(0, 10))} />
            </ReportMetaRow>
          </ReportMeta>
        </ReportHeader>
        <TableHead showUnknown={showUnknown} />
      </div>
    ),
    [nationalityCount, onboard.length, showUnknown, vesselCount, vesselLabel],
  );

  const continueHeader = useMemo(
    () => (
      <div className="ob-count-print-flow-header">
        <div className="ob-count-print-continue-bar">
          <span className="ob-count-print-continue-title">Onboard counts</span>
          <span className="ob-count-print-continue-meta">{vesselLabel}</span>
          <span className="ob-count-print-continue-hint">continued</span>
        </div>
        <TableHead showUnknown={showUnknown} />
      </div>
    ),
    [showUnknown, vesselLabel],
  );

  const lastFooter = useMemo(
    () => (
      <ReportFooter>
        <ReportSigns>
          <ReportSignLine label="Prepared by" />
          <ReportSignLine label="Date" />
        </ReportSigns>
        <ReportNote>
          Count of currently onboard crew (active ONB assignments) per vessel, nationality, and months
          onboard.
        </ReportNote>
      </ReportFooter>
    ),
    [],
  );

  const { items, itemKeys } = useMemo(() => {
    if (rows.length === 0) {
      return {
        items: [
          <p key="empty" className="ob-count-print-empty">
            No onboard crew for these filters.
          </p>,
        ],
        itemKeys: ['empty'],
      };
    }
    return {
      items: [
        ...rows.map((row) => (
          <LineRow key={row.key} row={row} showUnknown={showUnknown} />
        )),
        <TotalsRow key="totals" rows={rows} showUnknown={showUnknown} />,
      ],
      itemKeys: [...rows.map((row) => row.key), 'totals'],
    };
  }, [rows, showUnknown]);

  return (
    <ReportFlowPages
      className="ob-count-print"
      size="landscape"
      firstHeader={firstHeader}
      continueHeader={continueHeader}
      lastFooter={lastFooter}
      items={items}
      itemKeys={itemKeys}
    />
  );
};

export default OnboardCountReport;
